import Link from "next/link"
import {
  Calculator,
  FileSpreadsheet,
  Trophy,
  BookOpen,
  ArrowRight,
} from "lucide-react"

interface FreeResource {
  type: "calculator" | "template" | "comparison" | "glossary"
  slug: string
  label: string
  description: string
}

const typeConfig: Record<
  FreeResource["type"],
  { icon: any; path: string; badge: string; color: string }
> = {
  calculator: {
    icon: Calculator,
    path: "/calculadora",
    badge: "Calculadora",
    color: "lime",
  },
  template: {
    icon: FileSpreadsheet,
    path: "/plantillas",
    badge: "Plantilla",
    color: "orange",
  },
  comparison: {
    icon: Trophy,
    path: "/mejores",
    badge: "Comparativa",
    color: "sky",
  },
  glossary: {
    icon: BookOpen,
    path: "/glosario",
    badge: "Glosario",
    color: "emerald",
  },
}

interface FreeResourcesSectionProps {
  resources: FreeResource[]
  title?: string
  subtitle?: string
}

export default function FreeResourcesSection({
  resources,
  title = "Recursos gratuitos",
  subtitle = "Herramientas y contenidos para mejorar la gestión de tu restaurante.",
}: FreeResourcesSectionProps) {
  if (!resources || resources.length === 0) return null

  return (
    <section className="py-16 lg:py-20 px-6 lg:px-12 bg-zinc-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl lg:text-4xl font-medium text-zinc-900 tracking-tight mb-4">
            {title}
          </h2>
          <p className="text-lg text-zinc-600">{subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {resources.map((resource) => {
            const config = typeConfig[resource.type]
            const Icon = config.icon
            const href = `${config.path}/${resource.slug}`

            return (
              <Link
                key={`${resource.type}-${resource.slug}`}
                href={href}
                className="group bg-white rounded-2xl border border-zinc-200 hover:border-zinc-300 p-6 transition-all hover:shadow-lg hover:-translate-y-1"
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-${config.color}-50`}
                  >
                    <Icon className={`w-6 h-6 text-${config.color}-600`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span
                      className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium mb-2 bg-${config.color}-100 text-${config.color}-700`}
                    >
                      {config.badge}
                    </span>
                    <h3 className="font-semibold text-zinc-900 group-hover:text-lime-600 transition-colors mb-1">
                      {resource.label}
                    </h3>
                    <p className="text-sm text-zinc-600 line-clamp-2">
                      {resource.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-sm font-medium text-lime-600 mt-4 group-hover:text-lime-700">
                  Ver recurso
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </section>
  )
}

// Export common resources for each product page
export const horarioResources: FreeResource[] = [
  {
    type: "template",
    slug: "horarios-turnos",
    label: "Plantilla de Horarios y Turnos",
    description: "Cuadrante semanal para organizar turnos de mañana, tarde y noche.",
  },
  {
    type: "template",
    slug: "cuadrante-semanal",
    label: "Cuadrante Semanal",
    description: "Planifica la semana completa con turnos, descansos y horas totales.",
  },
  {
    type: "glossary",
    slug: "cuadrante-de-turnos",
    label: "¿Qué es un Cuadrante de Turnos?",
    description: "Definición, tipos de turno y normativa laboral en España.",
  },
  {
    type: "comparison",
    slug: "software-turnos-hosteleria",
    label: "Software de Turnos para Hostelería",
    description: "Comparativa de los 7 mejores software de gestión de turnos.",
  },
]

export const inventarioResources: FreeResource[] = [
  {
    type: "template",
    slug: "inventario-cocina",
    label: "Plantilla de Inventario de Cocina",
    description: "Control de stock: ingredientes, cantidades y caducidades.",
  },
  {
    type: "glossary",
    slug: "merma",
    label: "¿Qué es la Merma?",
    description: "Tipos de merma, porcentajes aceptables y cómo reducirla.",
  },
  {
    type: "calculator",
    slug: "food-cost",
    label: "Calculadora de Food Cost",
    description: "Calcula el porcentaje de coste de ingredientes de tus platos.",
  },
  {
    type: "comparison",
    slug: "herramientas-inventario-cocina",
    label: "Herramientas de Inventario",
    description: "Las 6 mejores herramientas de inventario para cocina.",
  },
]

export const produccionResources: FreeResource[] = [
  {
    type: "template",
    slug: "ficha-tecnica-plato",
    label: "Ficha Técnica de Plato",
    description: "Estandariza recetas con ingredientes, gramajes y costes.",
  },
  {
    type: "glossary",
    slug: "escandallo",
    label: "¿Qué es un Escandallo?",
    description: "Guía completa con ejemplo práctico de escandallo.",
  },
  {
    type: "glossary",
    slug: "food-cost",
    label: "¿Qué es el Food Cost?",
    description: "Fórmula, cálculo y ejemplos del indicador más importante.",
  },
  {
    type: "calculator",
    slug: "food-cost",
    label: "Calculadora de Food Cost",
    description: "Calcula el food cost de tus platos al instante.",
  },
]

export const empleadosResources: FreeResource[] = [
  {
    type: "glossary",
    slug: "ratio-personal-restaurante",
    label: "Ratio de Personal en Restaurante",
    description: "Cómo calcular cuántos empleados necesitas.",
  },
  {
    type: "calculator",
    slug: "coste-personal",
    label: "Calculadora de Coste de Personal",
    description: "Calcula el coste de personal por servicio.",
  },
  {
    type: "template",
    slug: "horarios-turnos",
    label: "Plantilla de Horarios y Turnos",
    description: "Cuadrante semanal para organizar turnos del equipo.",
  },
  {
    type: "comparison",
    slug: "software-turnos-hosteleria",
    label: "Software de Turnos",
    description: "Comparativa de software de gestión de personal.",
  },
]

export const facturacionResources: FreeResource[] = [
  {
    type: "template",
    slug: "control-costes",
    label: "Plantilla de Control de Costes",
    description: "Calcula el food cost y márgenes de tu carta.",
  },
  {
    type: "calculator",
    slug: "rentabilidad",
    label: "Calculadora de Rentabilidad",
    description: "Analiza ingresos, gastos y márgenes de tu negocio.",
  },
  {
    type: "glossary",
    slug: "coste-primo",
    label: "¿Qué es el Coste Primo?",
    description: "Fórmula y gestión del indicador clave de rentabilidad.",
  },
]

export const comprasResources: FreeResource[] = [
  {
    type: "template",
    slug: "control-costes",
    label: "Plantilla de Control de Costes",
    description: "Controla los costes de materias primas.",
  },
  {
    type: "glossary",
    slug: "merma",
    label: "¿Qué es la Merma?",
    description: "Tipos de merma y cómo reducir desperdicios.",
  },
  {
    type: "template",
    slug: "inventario-cocina",
    label: "Plantilla de Inventario",
    description: "Control de stock y gestión de proveedores.",
  },
]
